import { useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const retailerBadgeStyles = {
  walmart: "bg-blue-100 text-blue-800",
  loblaws: "bg-red-100 text-red-800",
  sobeys: "bg-green-100 text-green-800",
};

function LabelField({ label, value, onChange, readOnly = false }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700">{label}</label>
      <input
        type="text"
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm ${
          readOnly
            ? "bg-gray-50 text-gray-600"
            : "focus:border-teal-500 focus:ring-teal-500"
        }`}
      />
    </div>
  );
}

function SunShippingLabelPreview({ labelData, labels }) {
  const locationHeader = labels?.location_header ?? "DC NUMBER";
  const itemHeader = labels?.item_header ?? "WALMART ITEM #";
  const carrierText =
    labelData.carrier?.trim() !== ""
      ? `Carrier: ${labelData.carrier}`
      : "Carrier:";
  const billText =
    labelData.bill_of_lading?.trim() !== ""
      ? `B/L: ${labelData.bill_of_lading}`
      : "B/L: BIL#";
  const proText =
    labelData.pro_number?.trim() !== ""
      ? `PRO: ${labelData.pro_number}`
      : "PRO:";

  return (
    <div className="rounded border border-gray-300 bg-white p-2">
      <div
        className="mx-auto w-full max-w-md border border-black"
        style={{ aspectRatio: "152.4 / 101.6" }}
      >
        <table className="h-full w-full border-collapse text-[11px]">
          <tbody>
            <tr>
              <td className="border border-black p-1 align-top">
                <div className="font-bold">SHIP FROM:</div>
                <div>{labelData.ship_from?.name}</div>
                <div>{labelData.ship_from?.address}</div>
                <div>{labelData.ship_from?.city_province}</div>
                <div>{labelData.ship_from?.postal_code}</div>
              </td>
              <td className="border border-black p-1 align-top">
                <div className="font-bold">SHIP TO:</div>
                <div>{labelData.ship_to?.name}</div>
                <div>{labelData.ship_to?.address}</div>
                <div>{labelData.ship_to?.city_province}</div>
                <div>{labelData.ship_to?.postal_code}</div>
              </td>
            </tr>
            <tr>
              <td className="border border-black p-1 font-bold">PO NUMBER</td>
              <td className="border border-black p-1">{labelData.po_number}</td>
            </tr>
            <tr>
              <td className="border border-black p-1 font-bold">
                {locationHeader}
              </td>
              <td className="border border-black p-1">
                {labelData.location_id}
              </td>
            </tr>
            <tr>
              <td className="border border-black p-1 text-center font-bold">
                {itemHeader}
              </td>
              <td className="border border-black p-1">
                DATE CODE{" "}
                <span className="text-[8px]">
                  (Best Before/ Meilleur avant)
                </span>
              </td>
            </tr>
            <tr>
              <td className="border border-black p-1 text-center">
                {labelData.customer_item_number}
              </td>
              <td className="border border-black p-1">{labelData.date_code}</td>
            </tr>
            <tr>
              <td
                className="border border-black p-1 align-top font-bold"
                rowSpan={3}
              >
                Ship to Postal Code
              </td>
              <td className="border border-black p-1">{carrierText}</td>
            </tr>
            <tr>
              <td className="border border-black p-1">{billText}</td>
            </tr>
            <tr>
              <td className="border border-black p-1">{proText}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SunShippingLabelReviewModal(props) {
  const {
    setSlideOver,
    labelInfo,
    initialLabelData,
    orderItem,
    packagingItem,
    onPrint,
    onReset,
    isPrinting,
  } = props;

  const [labelData, setLabelData] = useState(initialLabelData);

  const retailerLabel = useMemo(() => {
    if (labelInfo.label_type === "sobeys") return "Sobeys";
    if (labelInfo.label_type === "loblaws") return "Loblaws";
    return "Walmart";
  }, [labelInfo.label_type]);

  const updateField = (field, value) => {
    setLabelData((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (group, field, value) => {
    setLabelData((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [field]: value,
      },
    }));
  };

  const handleReset = async () => {
    const resetData = await onReset();
    if (resetData) {
      setLabelData(resetData);
    }
  };

  const closePanel = () => {
    setSlideOver({
      childComponent: null,
      open: false,
      customClass: null,
    });
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="px-4 py-6 sm:px-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-base font-semibold leading-6 text-gray-900">
                Review shipping label
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {orderItem.product_name} · Lot {packagingItem.product_lot}
              </p>
              <span
                className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  retailerBadgeStyles[labelInfo.label_type] ??
                  retailerBadgeStyles.walmart
                }`}
              >
                {retailerLabel}
              </span>
            </div>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={closePanel}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="relative flex flex-1 flex-col gap-6 px-4 pb-6 sm:px-6 lg:flex-row">
        <div className="w-full space-y-4 lg:w-[45%]">
          <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
            <div>
              <span className="font-medium">Product:</span>{" "}
              {orderItem.product_name}
            </div>
            <div>
              <span className="font-medium">PLU:</span> {orderItem.plu}
            </div>
            <div>
              <span className="font-medium">Lot:</span>{" "}
              {packagingItem.product_lot}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Ship from</h4>
            <LabelField
              label="Name"
              value={labelData.ship_from?.name}
              readOnly
              onChange={() => {}}
            />
            <LabelField
              label="Address"
              value={labelData.ship_from?.address}
              readOnly
              onChange={() => {}}
            />
            <LabelField
              label="City, Province"
              value={labelData.ship_from?.city_province}
              readOnly
              onChange={() => {}}
            />
            <LabelField
              label="Postal code"
              value={labelData.ship_from?.postal_code}
              readOnly
              onChange={() => {}}
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Ship to</h4>
            <LabelField
              label="Location name"
              value={labelData.ship_to?.name}
              onChange={(value) => updateNestedField("ship_to", "name", value)}
            />
            <LabelField
              label="Street address"
              value={labelData.ship_to?.address}
              onChange={(value) =>
                updateNestedField("ship_to", "address", value)
              }
            />
            <LabelField
              label="City, Province"
              value={labelData.ship_to?.city_province}
              onChange={(value) =>
                updateNestedField("ship_to", "city_province", value)
              }
            />
            <LabelField
              label="Postal code"
              value={labelData.ship_to?.postal_code}
              onChange={(value) =>
                updateNestedField("ship_to", "postal_code", value)
              }
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Order / item</h4>
            <LabelField
              label="PO number"
              value={labelData.po_number}
              onChange={(value) => updateField("po_number", value)}
            />
            <LabelField
              label={labelInfo.labels?.location_header ?? "DC NUMBER"}
              value={labelData.location_id}
              onChange={(value) => updateField("location_id", value)}
            />
            <LabelField
              label={labelInfo.labels?.item_header ?? "Customer item #"}
              value={labelData.customer_item_number}
              onChange={(value) =>
                updateField("customer_item_number", value)
              }
            />
            <LabelField
              label="Date code (Best Before)"
              value={labelData.date_code}
              onChange={(value) => updateField("date_code", value)}
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Shipping details
            </h4>
            <LabelField
              label="Carrier"
              value={labelData.carrier}
              onChange={(value) => updateField("carrier", value)}
            />
            <LabelField
              label="Bill of lading"
              value={labelData.bill_of_lading}
              onChange={(value) => updateField("bill_of_lading", value)}
            />
            <LabelField
              label="PRO number"
              value={labelData.pro_number}
              onChange={(value) => updateField("pro_number", value)}
            />
          </div>
        </div>

        <div className="w-full lg:w-[55%]">
          <h4 className="mb-2 text-sm font-semibold text-gray-900">Preview</h4>
          <SunShippingLabelPreview
            labelData={labelData}
            labels={labelInfo.labels}
          />
        </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-end gap-3 px-4 py-4 sm:px-6">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={closePanel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleReset}
          disabled={isPrinting}
        >
          Reset to defaults
        </button>
        <button
          type="button"
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
          onClick={() => onPrint(labelData)}
          disabled={isPrinting}
        >
          {isPrinting ? "Printing..." : "Print label"}
        </button>
      </div>
    </div>
  );
}
